import { OOFProvidersEnum } from '../../../common/enums/oofproviders.enum';
import { OrderRoutes } from '../orders-routes.enum';
import { FlowContext, FlowDefinition } from './flow.types';

const hasAppointments = (ctx: FlowContext): boolean => {
    const orderDetails = ctx.orderStateService.getOrderQualificationDetails();
    const createOrderDetails = ctx.orderStateService.getCreateOrderDetails();
    return (orderDetails?.Appointments?.Appointment?.length > 0) ||
        (createOrderDetails?.Appointments?.Appointment?.length > 0);
};

const rccAvailable = (ctx: FlowContext): boolean =>
    ctx.orderQualificationResponseService.rcc1Available() ||
    ctx.orderQualificationResponseService.rcc2Available();

export const nextRouteBasedOnOQ = (ctx: FlowContext, response: any, skipAddressPreferences = false): string => {
    if (ctx.isResidentialSatelliteBillingAddressEnabled() && !skipAddressPreferences) {
        return OrderRoutes.ALTERNATE_SHIPPING;
    }

    if (ctx.hasTermsAndConditions(response)) {
        return OrderRoutes.TERMS_AND_CONDITIONS;
    }

    if (response?.Checklist?.Item?.length > 0) {
        return OrderRoutes.ORDER_CHECKLIST;
    }

    if (response.TPV || response.FPV) {
        return OrderRoutes.CUSTOMER_ORDER_VERIFICATION;
    }
    return OrderRoutes.PAYMENT_INFORMATION;
};

const verificationBackRoute = (ctx: FlowContext): string => {
    const oqResponse = ctx.orderQualificationDetails();
    if (oqResponse?.Checklist) {
        return OrderRoutes.ORDER_CHECKLIST;
    }
    if (oqResponse?.OrderTerms) {
        return OrderRoutes.TERMS_AND_CONDITIONS;
    }
    return OrderRoutes.ORDER_OPTIONS;
};

// Steps shared by every flow
export const COMMON_FLOW: FlowDefinition = [
    {
        route: OrderRoutes.ORDER_SETUP,
        next: OrderRoutes.SERVICE_INFORMATION,
        back: OrderRoutes.WELCOME
    },
    {
        route: OrderRoutes.SERVICE_INFORMATION,
        next: (ctx, data) => data?.systemDown ? OrderRoutes.ALL_SERVICES_DOWN
            : ctx.isCommercial() ? OrderRoutes.VIDEO_PACKAGE_SELECTION
            : OrderRoutes.BUNDLE_HOMEPAGE,
        back: OrderRoutes.ORDER_SETUP
    },
    {
        route: OrderRoutes.ALL_SERVICES_DOWN,
        next: OrderRoutes.ORDER_SETUP
    },
    {
        route: OrderRoutes.BUNDLE_HOMEPAGE,
        next: OrderRoutes.ORDER_OPTIONS,
        back: OrderRoutes.WELCOME
    },
    {
        route: OrderRoutes.CREDIT_CHECK,
        next: (ctx, data) => ctx.creditDecisionNextRoute(data?.creditCheckResponse),
        back: ctx => ctx.getFlowBackRoute()
    },
    {
        route: OrderRoutes.PREVIOUS_ADDRESS_COLLECTION,
        next: (ctx, data) => ctx.creditDecisionNextRoute(data?.creditCheckResponse, true),
        back: OrderRoutes.CREDIT_CHECK
    },
    {
        route: OrderRoutes.NCVC_QUESTIONNAIRE,
        next: (ctx, data) => ctx.creditDecisionNextRoute(data?.creditCheckResponse),
        back: ctx => ctx.getFlowBackRoute()
    },
    {
        route: OrderRoutes.ACCOUNT_REVIEW,
        next: (ctx, data) => ctx.creditDecisionNextRoute(data?.creditCheckResponse),
        back: OrderRoutes.CREDIT_CHECK
    },
    {
        route: OrderRoutes.OUTSTANDING_BALANCE,
        next: (ctx, data) => ctx.creditDecisionNextRoute(data?.creditCheckResponse),
        back: OrderRoutes.CREDIT_CHECK
    },
    {
        route: OrderRoutes.ORDER_OPTIONS,
        next: (ctx, data) => data?.oqResponse
            ? nextRouteBasedOnOQ(ctx, data.oqResponse)
            : OrderRoutes.ALTERNATE_SHIPPING,
        back: ctx => ctx.hasCompletedServices()
            ? OrderRoutes.BUNDLE_HOMEPAGE
            : OrderRoutes.PRODUCT_SUMMARY
    },
    {
        route: OrderRoutes.TERMS_AND_CONDITIONS,
        next: ctx => {
            const oqResponse = ctx.orderQualificationDetails();
            if (oqResponse?.Checklist) {
                return OrderRoutes.ORDER_CHECKLIST;
            }
            if (oqResponse?.TPV || oqResponse?.FPV) {
                return OrderRoutes.CUSTOMER_ORDER_VERIFICATION;
            }
            return OrderRoutes.PAYMENT_INFORMATION;
        },
        back: ctx => ctx.isResidentialSatelliteBillingAddressEnabled() ? OrderRoutes.ALTERNATE_SHIPPING
            : ctx.isCommercial() ? OrderRoutes.STREAM_HARDWARE_SELECTION
            : OrderRoutes.BUNDLE_HOMEPAGE
    },
    {
        route: OrderRoutes.ORDER_CHECKLIST,
        next: ctx => {
            const oqResponse = ctx.orderQualificationDetails();
            return (oqResponse?.TPV || oqResponse?.FPV)
                ? OrderRoutes.CUSTOMER_ORDER_VERIFICATION
                : OrderRoutes.PAYMENT_INFORMATION;
        },
        back: OrderRoutes.TERMS_AND_CONDITIONS
    },
    {
        route: OrderRoutes.CUSTOMER_ORDER_VERIFICATION,
        next: OrderRoutes.PAYMENT_INFORMATION,
        back: verificationBackRoute
    },
    {
        route: OrderRoutes.THIRD_PARTY_VERIFICATION,
        next: OrderRoutes.PAYMENT_INFORMATION,
        back: verificationBackRoute
    },
    {
        route: OrderRoutes.PAYMENT_INFORMATION,
        next: ctx => {
            if (hasAppointments(ctx) || rccAvailable(ctx)) {
                return ctx.isDtvCommercialSatelliteInstallationChecklistEnabled()
                    ? OrderRoutes.INSTALLATION_CHECKLIST
                    : OrderRoutes.INSTALLATION_SCHEDULING;
            }
            return ctx.getProvider().toLowerCase() === OOFProvidersEnum.SPECTRUM
                ? OrderRoutes.ORDER_REVIEW
                : OrderRoutes.ORDER_SUMMARY;
        },
        back: ctx => {
            const oqResponse = ctx.orderQualificationDetails();
            if (oqResponse?.FPV || oqResponse?.TPV) {
                return OrderRoutes.CUSTOMER_ORDER_VERIFICATION;
            }
            if (oqResponse?.Checklist) {
                return OrderRoutes.ORDER_CHECKLIST;
            }
            if (ctx.hasTermsAndConditions(oqResponse)) {
                return OrderRoutes.TERMS_AND_CONDITIONS;
            }
            return OrderRoutes.ORDER_OPTIONS;
        }
    },
    {
        route: OrderRoutes.INSTALLATION_CHECKLIST,
        next: OrderRoutes.INSTALLATION_SCHEDULING,
        back: OrderRoutes.PAYMENT_INFORMATION
    },
    {
        route: OrderRoutes.INSTALLATION_SCHEDULING,
        next: ctx => rccAvailable(ctx) ? OrderRoutes.REQUIRED_CALL
            : ctx.isCharter ? OrderRoutes.ORDER_REVIEW
            : OrderRoutes.ORDER_SUMMARY
    },
    {
        route: OrderRoutes.REQUIRED_CALL,
        next: OrderRoutes.ORDER_SUMMARY
    },
    {
        route: OrderRoutes.ORDER_REVIEW,
        next: OrderRoutes.ORDER_SUMMARY,
        back: ctx => hasAppointments(ctx) ? OrderRoutes.INSTALLATION_SCHEDULING : null
    }
];
